import React from 'react';
import { Typography } from '@loomstateorg/shared-frontend';
import Tile, { TileProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content/tile';
import type { TypographySlot } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/look-types';

export interface GridRow {
  icon: string;
  leftText: string;
  rightText: string;
}

export interface TextTileProps {
  icon: string;
  mainText: string;
  ctaText: string;
  ctaReference: string;
  resultsEco: GridRow[];
  tileContent: TileProps;
  ctaClassName?: string;
  titleFontFamily?: TypographySlot['fontFamily'];
  titleClassName?: string;
  tileTitleFontFamily?: TypographySlot['fontFamily'];
  tileCtaFontFamily?: TypographySlot['fontFamily'];
  tileCtaClassName?: string;
  children?: React.ReactNode;
}

const TextTile: React.FC<TextTileProps> = ({
  icon,
  mainText,
  ctaText,
  ctaReference,
  resultsEco,
  tileContent,
  ctaClassName = 'mt-10 rounded-md border-2 border-eco-light bg-white px-12 py-6 font-secondaryBold text-14 text-eco',
  titleFontFamily = 'primaryBold',
  titleClassName = 'text-lg font-bold',
  tileTitleFontFamily,
  tileCtaFontFamily,
  tileCtaClassName,
}) => {
  return (
    <div className="mt-52 flex flex-wrap items-center gap-16 lg:flex-nowrap" data-testid="eco-text-tile">
      <div className="mx-0 w-full text-eco lg:mx-52 lg:w-1/2">
        <div className="flex flex-col gap-4">
          <img src={icon} alt="Icon" className="size-40" />
          <Typography as="span" className={titleClassName} fontFamily={titleFontFamily} fontSize={28}>
            {mainText}
          </Typography>
        </div>
        <a href={ctaReference} data-testid="eco-text-tile-cta">
          {/* testid-ignore: not focusable (tabIndex -1); the wrapping eco-text-tile-cta anchor handles navigation */}
          <button tabIndex={-1} className={ctaClassName}>
            {ctaText}
          </button>
        </a>
        <div className="mt-36 lg:mt-90">
          {resultsEco?.map((row, index) => (
            <div
              key={index}
              className={`${
                index !== resultsEco.length - 1 ? 'mb-4 border-b border-dashed border-eco pb-4' : ''
              } flex items-center justify-between`}
              data-testid={`eco-result-${index}`}
            >
              <div className="flex items-center gap-4">
                <img src={row.icon} alt="Row Icon" className="size-30" />
                <Typography as="span" fontFamily="IBMPlexMonoRegular" fontSize={28}>
                  {row.leftText}
                </Typography>
              </div>
              <Typography as="span" fontFamily={titleFontFamily} fontSize={12}>
                {row.rightText}
              </Typography>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full lg:w-1/2" data-testid="eco-text-tile-banner">
        <Tile
          image={tileContent.image}
          title={tileContent.title}
          subtitle={tileContent.subtitle}
          ctaLabel={tileContent.ctaLabel}
          ctaReference={tileContent.ctaReference}
          className="mb-8 mt-52 h-296 md:mb-0 md:h-[532px] lg:mt-0 lg:h-[668px]"
          alignment="left"
          titleFontFamily={tileTitleFontFamily}
          ctaFontFamily={tileCtaFontFamily}
          ctaClassName={tileCtaClassName}
        />
      </div>
    </div>
  );
};

export default TextTile;
